import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, set, get } from 'firebase/database';
import { getAuth, signInWithPopup, GoogleAuthProvider, onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import * as promptpay from 'promptparse';
import firebaseConfig from '../firebase-config.json';

// Initialize Firebase with error handling
let app;
let db;
let auth;
let provider;

try {
  app = initializeApp(firebaseConfig);
  db = getDatabase(app);
  auth = getAuth(app);
  provider = new GoogleAuthProvider();
  // Add scopes for Google provider
  provider.addScope('profile');
  provider.addScope('email');
  // Force account selection
  provider.setCustomParameters({
    prompt: 'select_account'
  });
} catch (error) {
  console.error('Error initializing Firebase:', error);
}

const App = () => {
  const [userId, setUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [orderId, setOrderId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [slipUrl, setSlipUrl] = useState('');
  const [promptpaySlip, setPromptpaySlip] = useState('');
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!auth) return;

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        console.log('User is signed in:', currentUser);
        setUser(currentUser);
        setUserId(currentUser.uid);
        setError(null);
      } else {
        console.log('User is signed out');
        setUser(null);
        setUserId('');
      }
    });

    return () => unsubscribe();
  }, []);

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!auth || !provider) {
        throw new Error('Firebase authentication not initialized properly');
      }

      console.log('Starting Google sign-in process...');
      const result = await signInWithPopup(auth, provider);
      console.log('Sign-in successful:', result.user);
      
      setUser(result.user);
      setUserId(result.user.uid);
      setError(null);
    } catch (error) {
      console.error('Detailed login error:', error);
      
      // Handle specific error cases
      switch (error.code) {
        case 'auth/popup-blocked':
          setError('Please allow popups for this website and try again.');
          break;
        case 'auth/cancelled-popup-request':
          setError('Login was cancelled. Please try again.');
          break;
        case 'auth/popup-closed-by-user':
          setError('Login popup was closed. Please try again.');
          break;
        case 'auth/unauthorized-domain':
          setError('This domain is not authorized for Google login. Please contact support.');
          break;
        default:
          setError(`Login failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      setUser(null);
      setUserId('');
      setOrderId(null);
      setPaymentStatus('');
      setError(null);
    } catch (error) {
      console.error('Logout error:', error);
      setError('Failed to logout. Please try again.');
    }
  };

  const createOrder = async () => {
    if (!userId || !amount) {
      setError('Please provide both User ID and Amount!');
      return;
    }

    setIsLoading(true);
    try {
      const orderId = `NEP-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const orderRef = ref(db, `orders/${orderId}`);
      
      await set(orderRef, {
        userId,
        amount: parseFloat(amount),
        status: 'pending',
        createdAt: Date.now(),
      });
      
      setOrderId(orderId);
      setPaymentStatus('Order created. Please upload your payment slip.');
      setError(null);
    } catch (error) {
      console.error('Error creating order:', error);
      setError('Failed to create order. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyPromptPay = async () => {
    if (!promptpaySlip || !orderId) {
      setError('Please upload a PromptPay slip and create an order first!');
      return;
    }

    setIsLoading(true);
    try {
      const { isValid, data } = await promptpay.verify(promptpaySlip);
      if (isValid) {
        const orderRef = ref(db, `orders/${orderId}`);
        await set(orderRef, {
          status: 'paid',
          paymentDetails: data,
          paymentMethod: 'promptpay',
          verifiedAt: Date.now(),
        });
        setPaymentStatus('PromptPay payment verified successfully.');
        setError(null);
      } else {
        setError('PromptPay verification failed. Invalid slip.');
      }
    } catch (error) {
      console.error('PromptPay verification error:', error);
      setError('Error during PromptPay verification. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyPayment = async () => {
    if (!slipUrl || !orderId) {
      setError('Please upload a payment slip and create an order first!');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post('https://api.paymentgateway.com/verify', {
        orderId,
        slipUrl
      });

      if (response.data.status === 'success') {
        const orderRef = ref(db, `orders/${orderId}`);
        await set(orderRef, {
          status: 'paid',
          paymentDetails: response.data.paymentDetails,
          paymentMethod: 'slip',
          verifiedAt: Date.now(),
        });
        setPaymentStatus('Payment verified successfully.');
        setError(null);
      } else {
        setError('Payment verification failed.');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setError('Error during payment verification. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!orderId || !db) return;

    const orderRef = ref(db, `orders/${orderId}`);
    const checkPaymentStatus = async () => {
      try {
        const snapshot = await get(orderRef);
        const orderData = snapshot.val();
        if (orderData?.status === 'paid') {
          setPaymentStatus('Payment verified successfully.');
          setError(null);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    };

    checkPaymentStatus();
  }, [orderId]);

  return (
    <div className="min-h-screen p-4 bg-gray-50">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-center">Payment Verification System</h1>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {!user ? (
          <button
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className={`w-full ${isLoading ? 'bg-gray-400' : 'bg-blue-500 hover:bg-blue-600'} text-white py-2 px-4 rounded transition-colors`}
          >
            {isLoading ? 'Logging in...' : 'Login with Google'}
          </button>
        ) : (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Welcome, {user.displayName}</h2>
                <button
                  onClick={handleLogout}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Logout
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">User ID:</label>
                  <input
                    type="text"
                    value={userId}
                    disabled
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount:</label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter Payment Amount"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>

                <button
                  onClick={createOrder}
                  disabled={isLoading}
                  className={`w-full ${isLoading ? 'bg-gray-400' : 'bg-green-500 hover:bg-green-600'} text-white py-2 px-4 rounded transition-colors`}
                >
                  {isLoading ? 'Creating Order...' : 'Create Order'}
                </button>
              </div>
            </div>

            {orderId && (
              <div className="bg-white p-6 rounded-lg shadow space-y-6">
                <div>
                  <h2 className="text-xl font-semibold mb-4">Upload Payment Slip</h2>
                  <input
                    type="text"
                    value={slipUrl}
                    onChange={(e) => setSlipUrl(e.target.value)}
                    placeholder="Enter payment slip URL"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                  <button
                    onClick={verifyPayment}
                    disabled={isLoading}
                    className={`mt-2 w-full ${isLoading ? 'bg-gray-400' : 'bg-blue-500 hover:bg-blue-600'} text-white py-2 px-4 rounded transition-colors`}
                  >
                    {isLoading ? 'Verifying...' : 'Verify Payment'}
                  </button>
                </div>

                <div>
                  <h2 className="text-xl font-semibold mb-4">Upload PromptPay Slip</h2>
                  <input
                    type="text"
                    value={promptpaySlip}
                    onChange={(e) => setPromptpaySlip(e.target.value)}
                    placeholder="Enter PromptPay slip"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                  <button
                    onClick={verifyPromptPay}
                    disabled={isLoading}
                    className={`mt-2 w-full ${isLoading ? 'bg-gray-400' : 'bg-blue-500 hover:bg-blue-600'} text-white py-2 px-4 rounded transition-colors`}
                  >
                    {isLoading ? 'Verifying...' : 'Verify PromptPay'}
                  </button>
                </div>

                <div className="mt-4">
                  <h3 className="text-lg font-medium">Payment Status:</h3>
                  <p className="mt-1 text-gray-600">{paymentStatus}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
